import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { HttpUtilsService, QueryParamsModel, QueryResultsModel } from '../../core/_base/crud';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../core/reducers';
import { currentAuthToken, currentUser } from '../../core/auth';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
// import {Notification, NotificationCategory, NotificationChannel, NotificationCriteria, NotificationCriteriaSettings} from '';
import { NotificationCriteriaResponse } from '../../core/lists/_models/NotificationCriteriaResponse.model';
import {
  NotificationCategory,
  NotificationChannel,
  NotificationCriteria,
  NotificationCriteriaSettings,
  Notification,
} from 'src/app/notification/models/notification.model';

@Injectable()
export class NotificationService {
  private companyId;
  private authToken;
  private headers;
  private baseUrl = environment.NOTIFICATION_BASE;
  private userId;
  private level;
  constructor(
    private http: HttpClient,
    private httpUtils: HttpUtilsService,
    private store: Store<AppState>,
  ) {
    this.store
      .select(currentAuthToken)
      .subscribe((res) => (this.headers = this.httpUtils.getHTTPHeaders(res)));
    this.store.pipe(select(currentAuthToken)).subscribe((res) => (this.authToken = res));
    this.store.pipe(select(currentUser)).subscribe((user) => {
      this.companyId = user.companyID;
      this.userId = user.id;
    });
  }
  private selectedChannelsSubject = new BehaviorSubject<string[]>([]);
  selectedChannels$ = this.selectedChannelsSubject.asObservable();

  private getNotificationQueryString(queryParams): string {
    const { pageNumber, pageSize, sortField, sortOrder, filter, level, id } = queryParams;
    this.level = level;
    this.userId = id;
    let fromString = `page=${pageNumber}&size=${pageSize}&sort=${sortField},${sortOrder}`;
    if (filter.searchTerm) {
      fromString += `&search_term=${filter.searchTerm}`;
    }

    fromString += `&level=${this.level}&related_id=${this.userId}`;
    if (filter.description) fromString += `&description_has=${filter.description}`;
    if (filter.source) fromString += `&source_has=${filter.source}`;
    return fromString;
  }
  setSelectedChannels(channels: string[]): void {
    this.selectedChannelsSubject.next(channels);
  }

  getSelectedChannels(): string[] {
    return this.selectedChannelsSubject.getValue();
  }
  getNotificationsPage(query: QueryParamsModel): Observable<QueryResultsModel> {
    const { pageSize, pageNumber, filter: searchData, sortField, sortOrder } = query;
    const fromString = `page=${pageNumber}&size=${pageSize}&search_term=${searchData}&sort=${sortField},${sortOrder}`;
    const params = new HttpParams({ fromString });

    return this.http.get<QueryResultsModel>(`${this.baseUrl}/notifications`, {
      headers: this.headers,
      params,
    });
  }
  getCompanyNotifications(queryParams: QueryParamsModel): Observable<QueryResultsModel> {
    const params = new HttpParams({ fromString: this.getNotificationQueryString(queryParams) });

    if (!this.companyId) {
      return this.http.get<QueryResultsModel>(`${this.baseUrl}/notifications`, {
        headers: this.headers,
        params,
      });
    } else {
      return this.http.get<QueryResultsModel>(
        `${this.baseUrl}/companies/${this.companyId}/notifications`,
        { headers: this.headers, params },
      );
    }
  }

  getCategories(companyId = '', term = ''): Observable<NotificationCategory[]> {
    const fromString = `company_id=${companyId}&term=${term}`;
    const params = new HttpParams({ fromString });

    return this.http.get<NotificationCategory[]>(`${this.baseUrl}/categories/autocomplete`, {
      headers: this.headers,
      params,
    });
  }

  getChannels(term = ''): Observable<NotificationChannel[]> {
    const fromString = `term=${term}`;
    const params = new HttpParams({ fromString });

    return this.http.get<NotificationChannel[]>(`${this.baseUrl}/channels/autocomplete`, {
      headers: this.headers,
      params,
    });
  }
  getCriteria(term = ''): Observable<NotificationCriteria[]> {
    const fromString = `term=${term}`;
    const params = new HttpParams({ fromString });

    return this.http.get<NotificationCriteria[]>(`${this.baseUrl}/notifications/criteria-keys`, {
      headers: this.headers,
      params,
    });
  }
  createCriteriaNotification(
    notificationCriteriaSettings: NotificationCriteriaSettings,
  ): Observable<HttpResponse<NotificationCriteriaSettings>> {
    return this.http.post<NotificationCriteriaSettings>(
      `${this.baseUrl}/notification-criteria_setting`,
      notificationCriteriaSettings,
      {
        headers: this.headers,
        observe: 'response',
      },
    );
  }
  createNotification(notification: Notification): Observable<HttpResponse<Notification>> {
    return this.http.post<Notification>(`${this.baseUrl}/notifications`, notification, {
      headers: this.headers,
      observe: 'response',
    });
  }
  updateNotification(notification: Notification): Observable<HttpResponse<Notification>> {
    return this.http.put<Notification>(
      `${this.baseUrl}/notifications/${notification.id}`,
      notification,
      {
        headers: this.headers,
        observe: 'response',
      },
    );
  }
  createNotificationWithCritiera(notification: any): Observable<any> {
    return this.http.post<Notification>(`${this.baseUrl}/notifications/create`, notification, {
      observe: 'response',
    });
  }

  updateNotificationWithCritiera(notificationId: number, notification: any): Observable<any> {
    return this.http.put<Notification>(
      `${this.baseUrl}/notifications/update/${notificationId}`,
      notification,
      { observe: 'response' },
    );
  }
  updateNotificationCriteria(notificationId: number, criteria: any[]): Observable<string> {
    return this.http.put(
      `${this.baseUrl}/notifications/${notificationId}/criteria_setting`,
      criteria,
      {
        responseType: 'text',
      },
    );
  }

  getNotificationCriteria(notificationId: number): Observable<NotificationCriteriaResponse> {
    return this.http.get<NotificationCriteriaResponse>(
      `${this.baseUrl}/notifications/${notificationId}/criteria_setting`,
    );
  }

  createNotificationCriteria(
    notificationId: number,
    criteria: { notificationCriteriaId: number }[],
  ): Observable<HttpResponse<any>> {
    return this.http.post<any>(
      `${this.baseUrl}/notifications/${notificationId}/criteria_settings`,
      criteria,
      {
        headers: this.headers,
        observe: 'response',
      },
    );
  }

  getNotificationById(notification: Notification): Observable<HttpResponse<Notification>> {
    return this.http.get<Notification>(`${this.baseUrl}/notifications/${notification.id}`, {
      headers: this.headers,
      observe: 'response',
    });
  }
  deleteNotification(id: number): Observable<HttpResponse<Notification>> {
    return this.http.delete<Notification>(`${this.baseUrl}/notifications/${id}`, {
      headers: this.headers,
      observe: 'response',
    });
  }
  updateNotificationSettings(body): Observable<any> {
    return this.http.put(`${this.baseUrl}/notification-setting`, body);
  }
  getNotificationSettings(): Observable<any> {
    return this.http.get(`${this.baseUrl}/notification-setting`);
  }
  getPreSignedURL(attachmentName): Observable<any> {
    return this.http.post(`${this.baseUrl}/notification-setting/attachment`, {
      name: attachmentName,
    });
  }
}
